//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useEffect, useState, useContext } from 'react';
import { Grid, makeStyles } from '@material-ui/core';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { logPageView } from '../system/firebase';
import { AppContext, LayoutContext } from '../common/contexts';
import { setClassNameInDefns } from '../common/definitionHelper';
import { getNewTalentCenterQuery } from '../common/queryHelper';
import { ELASTICSEARCH_SORT_OPTIONS, ELASTICSEARCH_SORT_OPTION_UPDATE_DATE_DESC } from '../common/elasticSearchHelper';
import { PAGE_TITLE_TALENT_CENTER } from '../common/constants';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { loadRecentSearches } from '../common/services/talentCenterServices';
// import { loadAdvertisementsByPage } from '../common/services/adServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdPageHead from '../common/elemental/AfitdPageHead';
// import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdAdContainer from '../common/elemental/AfitdAdContainer';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import TopNav from '../topNav/TopNav';
import SideBar from './subComponents/Sidebar';
// import FiltersBar from './subComponents/FilterBar';
// import SearchInput from './subComponents//SearchInput';
import JobSeekerProfile from './subComponents/JobSeekerProfile';
import Footer from './subComponents/Footer';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
// import _get from 'lodash/get';
import _filter from 'lodash/filter';
import _omit from 'lodash/omit';
import _isEqual from 'lodash/isEqual';
// import minutesToMilliseconds from 'date-fns/minutesToMilliseconds';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const { searchedPerformed } = params;

    return makeStyles(theme => ({
        talentCenterContainer: {
            backgroundColor: '#FAFAFC',
            overflowY: 'auto',
            height: '100%',
            minHeight: '100%',
            flexWrap: 'unset',
            paddingBottom: 20,
        },
        talentCenterHeader: { maxHeight: '81px' },
        talentCenterContent: {
            flex: 1,
            overflowY: 'hidden',
            paddingLeft: '64px',
            paddingRight: '64px',
            height: '100%',
            [theme.breakpoints.down('sm')]: { paddingLeft: '0px', paddingRight: '0px', marginTop: 0, overflowY: 'scroll' },
        },
        talentCenterLeft: {
            height: '100%',
            paddingTop: '25px',
            [theme.breakpoints.down('lg')]: {
                height: searchedPerformed ? '100%' : 'fit-content',
            },
        },
        talentCenterRight: {
            // overflowY: 'hidden',
            height: '100%',
            flexWrap: 'unset',
            // paddingTop: '35px',
            // minHeight: '600px',
            [theme.breakpoints.down('lg')]: { paddingTop: '0px' },
        },
        // talentCenterRight: {
        //     height: '100%',
        //     overflowY: 'hidden',
        //     flexWrap: 'unset',
        //     paddingTop: '35px',
        //     minHeight: '600px',
        //     [theme.breakpoints.down('lg')]: { paddingTop: '10px' },
        // },
        adContainerGrid: {
            // overflowY: 'hidden',
            height: '100%',
            flexWrap: 'unset',
            paddingTop: '35px',
            // minHeight: '600px',
            [theme.breakpoints.down('lg')]: { paddingTop: '0px' },
        },
        talentFilterContainer: {
            paddingLeft: '64px',
            paddingRight: '64px',
            [theme.breakpoints.only('md')]: {
                paddingLeft: '52px',
                paddingRight: '52px',
            },
            [theme.breakpoints.down('sm')]: {
                paddingLeft: '15px',
                paddingRight: '15px',
            },
        },
        talentFilterLeft: { paddingTop: '25px', [theme.breakpoints.down('sm')]: { paddingTop: '10px' } },
        talentFilterRight: { paddingTop: '25px', [theme.breakpoints.down('lg')]: { paddingTop: '10px' } },
        jobSeekerProfileContainer: {
            height: '100%',
            padding: '20px 0 0 0',
            marginLeft: '20px',
            marginTop: '22px',
            overflowY: 'hidden',
            [theme.breakpoints.down('lg')]: { marginTop: '0px', padding: '0px' },
            [theme.breakpoints.down('md')]: { marginLeft: '0px' },
        },
        bottomFooterContainer: { [theme.breakpoints.up('md')]: { paddingLeft: '64px', paddingRight: '64px' } },
        navigation: { padding: '16px 0px 16px 20px', [theme.breakpoints.down('md')]: { padding: '16px 0px 16px 12px' } },
        navigationButtonContainer: { display: 'flex', alignItems: 'center' },
        navigationIconContainer: {
            width: '32px',
            height: '32px',
            background: '#F2F2F4',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            '&:hover': { cursor: 'pointer' },
        },
        navIconLeft: { marginLeft: 11, width: 18 },
        navigationText: { marginLeft: 8, fontSize: 16, fontWeight: 'bold', '&:hover': { cursor: 'pointer' }, marginTop: '-2px' },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        handleDeleteChip,
        // sortTag,
        setSortTag,
        selected,
        setSelected,
        query,
        setQuery,
        isDefaultSearch,
        handleClickJobSeeker,
        showFilterPage,
        setShowFilterPage,
        showAds,
        setShowAds,
        searchedPerformed,
        setSearchPerformed,
    } = params;

    const defns = {
        talentCenterContainer: { container: true, direction: 'column' },
        talentCenterHeader: { item: true, lg: 12, md: 12, sm: 12, xs: 12 },
        talentCenterContent: { item: true, container: true },
        talentCenterLeft: { item: true, xl: 4, lg: 12, md: 12, sm: 12, xs: 12 },
        talentCenterRight: {
            item: true,
            container: true,
            direction: 'column',
            xl: 8,
            lg: 12,
            md: 12,
            sm: 12,
            xs: 12,
        },
        adContainerGrid: {
            item: true,
            container: true,
            direction: 'column',
            xl: 8,
            lg: 12,
            md: 12,
            sm: 12,
            xs: 12,
        },
        adContainer: {
            isDefaultSearch,
        },
        talentFilterContainer: { container: true, justifyContent: 'center', alignItems: 'center' },
        talentFilterLeft: { item: true, xl: 6, lg: 12, md: 12, sm: 12, xs: 12 },
        talentFilterRight: { item: true, xl: 6, lg: 12, md: 12, sm: 12, xs: 12 },
        navigation: { container: true, direction: 'row' },
        navigationItem: { item: true, xl: 3, lg: 4, md: 11, sm: 12, xs: 12 },
        bottomFooterContainer: { container: true, direction: 'row', justify: 'center' },
        bottomFooter: { item: true, xl: 12, lg: 12, md: 12, sm: 12, xs: 12 },
        jobSeekerProfile: { jobSeeker: selected, setSelected, isShowEmployerActions: true },
        container: {
            maxWidth: 'xl',
        },
        root: {
            container: true,
            spacing: 2,
        },
        sidebarContainer: {
            item: true,
            lg: 3,
            md: 12,
            sm: 12,
            xs: 12,
        },
        contentContainer: {
            item: true,
            lg: 9,
            md: 12,
            sm: 12,
            xs: 12,
        },
        sidebar: {
            selected,
            onDeleteChip: handleDeleteChip,
            setSelected,
            query,
            setQuery,
            onClickItem: handleClickJobSeeker,
            showFilterPage,
            setShowFilterPage,
            showAds,
            setShowAds,
            searchedPerformed,
            setSearchPerformed,
        },
        filterBar: {
            queryInitState,
            query,
            setQuery,
        },
        searchInput: {
            query,
            setQuery,
            setSortTag,
        },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const queryInitState = {
    jobType: [],
    salaryRate: null,
    occupation: null,
    location: null,
    //TODO Remove this when we are not loading all jobs on page load
    sortTag: ELASTICSEARCH_SORT_OPTIONS[ELASTICSEARCH_SORT_OPTION_UPDATE_DATE_DESC],
};
// const timeInterval = minutesToMilliseconds(30);

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const TalentCenterLayout = () => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const { userId } = appContext;
    const layoutContext = useContext(LayoutContext);
    const theme = useTheme();
    const lgMatches = useMediaQuery(theme.breakpoints.down('lg'));
    const mdMatches = useMediaQuery(theme.breakpoints.down('md'));
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    //TODO Change this back to ELASTICSEARCH_SORT_OPTION_MOST_RELEVANT when we are not loading all jobs on page load
    const [sortTag, setSortTag] = useState(ELASTICSEARCH_SORT_OPTION_UPDATE_DATE_DESC);
    // const [sortTag, setSortTag] = useState(ELASTICSEARCH_SORT_OPTION_MOST_RELEVANT);
    const [selected, setSelected] = useState(null);
    const [query, setQuery] = useState(queryInitState);
    const [searchResultTotal, setSearchResultTotal] = useState();
    const [showDetails, setShowDetails] = useState(false);
    const [showFilterPage, setShowFilterPage] = useState(false);
    const [recentSearches, setRecentSearches] = useState([]);
    const [requestRefreshSavedToJobs, setRequestRefreshSavedToJobs] = useState();
    const [showAds, setShowAds] = useState(true);
    const [searchedPerformed, setSearchPerformed] = useState(false);
    const [hideSidebar, setHideSideBar] = useState(false);
    const [advertisements] = useState([]);
    const [asyncCallCount, setAsyncCallCount] = React.useState(0);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const isDefaultSearch = _isEqual(_omit(query, ['sortTag']), _omit(queryInitState, ['sortTag']));

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        logPageView(PAGE_TITLE_TALENT_CENTER);
    }, []);

    // // load Advertisements
    // useEffect(() => {
    //     loadAdvertisementsByPage({ talentCtr: true, callBack: onServiceCallBack });
    //     setInterval(() => {
    //         loadAdvertisementsByPage({ talentCtr: true, callBack: onServiceCallBack });
    //     }, timeInterval);
    // }, []);

    // load recent searches
    useEffect(() => {
        let unsubscribeLoadRecentSearches = null;
        if (userId) {
            unsubscribeLoadRecentSearches = loadRecentSearches({ userId, setData: setRecentSearches });
        }
        return () => {
            unsubscribeLoadRecentSearches && unsubscribeLoadRecentSearches();
        };
    }, [userId]);

    //---------------------------------------------------------------------
    // CallBacks / Event handlers
    //---------------------------------------------------------------------
    const handleDeleteChip = (key, value) => {
        switch (key) {
            case 'willingRelocate': {
                setQuery(prevQuery => getNewTalentCenterQuery({ prevQuery, newQuery: { willingRelocate: undefined } }));
                return;
            }
            case 'jobType': {
                setQuery(prevQuery => getNewTalentCenterQuery({ prevQuery, newQuery: { jobType: _filter(prevQuery.jobType, item => item !== value) } }));
                return;
            }
            case 'salaryRate': {
                setQuery(prevQuery => getNewTalentCenterQuery({ prevQuery, newQuery: { salaryRate: null } }));
                return;
            }
            default:
            // do nothing
        }
    };

    const handleClickJobSeeker = () => {
        // console.log('is clicking');
        if (lgMatches) {
            setHideSideBar(true);
        }
        setShowAds(false);
        setShowDetails(true);
    };

    const handleClickBackArrow = () => {
        setShowDetails(false);
        setHideSideBar(false);
    };

    // const onServiceCallBack = data => {
    //     setAdvertisements(data);
    // };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const classes = useStyles({ searchedPerformed })();

    const defns = getDefns({
        classes,
        handleDeleteChip,
        sortTag,
        setSortTag,
        selected,
        setSelected,
        query,
        setQuery,
        isDefaultSearch,
        setShowDetails,
        showFilterPage,
        setShowFilterPage,
        handleClickJobSeeker,
        showAds,
        setShowAds,
        searchedPerformed,
        setSearchPerformed,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <LayoutContext.Provider
            value={{
                ...layoutContext,
                recentSearches,
                setRecentSearches,
                queryInitState,
                requestRefreshSavedToJobs,
                setRequestRefreshSavedToJobs,
                searchResultTotal,
                setSearchResultTotal,
                setAsyncCallCount,
            }}
        >
            <AfitdPageHead title={PAGE_TITLE_TALENT_CENTER} />
            <Grid {...defns.talentCenterContainer}>
                {smMatches && showFilterPage ? null : (
                    <Grid {...defns.talentCenterHeader}>
                        <TopNav isShowProgressBar={asyncCallCount > 0} />
                    </Grid>
                )}
                {/* <Grid {...defns.talentFilterContainer}>
                    <Grid {...defns.talentFilterLeft}>
                        <SearchInput {...defns.searchInput} />
                    </Grid>
                    {!smMatches && (
                        <Grid {...defns.talentFilterRight}>
                            <FiltersBar {...defns.filterBar} />
                        </Grid>
                    )}
                </Grid> */}

                {/* ----------------------------- Job Seeker Profile View --------------------------------*/}
                <Grid {...defns.talentCenterContent}>
                    {/* {showDetails ? (
                        <Grid {...defns.talentCenterRight}>
                            <Grid {...defns.navigation}>
                                <Grid {...defns.navigationItem}>
                                    <div {...defns.navigationButtonContainer} onClick={() => setShowDetails(false)}>
                                        <div {...defns.navigationIconContainer}>
                                            <ArrowBackIosIcon {...defns.navIconLeft} />
                                        </div>
                                        <span {...defns.navigationText}>Back</span>
                                    </div>
                                </Grid>
                            </Grid>

                            <div {...defns.jobSeekerProfileContainer}>
                                <JobSeekerProfile {...defns.jobSeekerProfile} />
                            </div>
                        </Grid> */}
                    {/* // ) : ( */}
                    {!hideSidebar && (
                        <Grid {...defns.talentCenterLeft}>
                            <SideBar {...defns.sidebar} />
                        </Grid>
                    )}

                    {/* // )} */}
                    {/* {lgMatches ? null : ( */}
                    {showAds ? (
                        mdMatches ? null : (
                            <Grid {...defns.adContainerGrid}>
                                {/* -----------------------------Display Ads Container---------------------------------- */}
                                <AfitdAdContainer {...defns.adContainer} data={advertisements} searchResultTotal={searchResultTotal} isTalentCenter />
                                {/* -----------------------------Display Ads Container---------------------------------- */}
                            </Grid>
                        )
                    ) : (
                        !showAds &&
                        showDetails && (
                            <Grid {...defns.talentCenterRight}>
                                {(smMatches || mdMatches || lgMatches) && (
                                    <Grid {...defns.navigation}>
                                        <Grid {...defns.navigationItem}>
                                            <div {...defns.navigationButtonContainer} onClick={handleClickBackArrow}>
                                                <div {...defns.navigationIconContainer}>
                                                    <ArrowBackIosIcon {...defns.navIconLeft} />
                                                </div>
                                                <span {...defns.navigationText}>Back</span>
                                            </div>
                                        </Grid>
                                    </Grid>
                                )}

                                <div {...defns.jobSeekerProfileContainer}>
                                    <JobSeekerProfile {...defns.jobSeekerProfile} />
                                </div>
                            </Grid>
                        )
                    )}
                    {/* )} */}
                </Grid>
                {/* ----------------------------- Job Seeker Profile View --------------------------------*/}

                {smMatches && showFilterPage ? null : (
                    <Grid {...defns.bottomFooterContainer}>
                        <Grid {...defns.bottomFooter}> {lgMatches ? <Footer /> : null}</Grid>
                    </Grid>
                )}
            </Grid>
        </LayoutContext.Provider>
    );
};

export default TalentCenterLayout;
